import { useEffect } from 'react';
import $kaizen from 'app/stores/ai/$kaizen';

/**
 * Hook that automatically adds AI context when component mounts and removes it when component unmounts.
 * This is useful for setting up context for the Kentik AI Copilot.
 * For interactions, such as device details, interface information, or cloud resources.
 * The context you provide will be provided as a prompt to the AI system, allowing it to understand the current state and provide relevant suggestions or actions.
 * See /node/services/kaizen/addChatMessage
 *
 * @param {Object} context - The context object to add to the AI system
 * @param {string} context.type - The type of context (e.g., 'device', 'interface', 'cloud_vpc')
 * @param {string} context.name - The unique name/identifier for this context
 * @param {string} context.context - The detailed context information
 * @param {Object} [context.model] - Optional model object associated with this context
 * @param {Array<string>} [context.suggestions] - Optional array of suggested prompts
 *
 * @example
 * // Basic usage
 * useAiContext({
 *   type: 'device',
 *   name: 'router-01',
 *   context: 'Router details: IP 192.168.1.1, Status: Active'
 * });
 *
 * @example
 * // With suggestions
 * useAiContext({
 *   type: 'interface',
 *   name: 'eth0',
 *   context: 'Interface eth0 on router-01: Speed 1Gbps, Status: Up',
 *   suggestions: [
 *     'Show bandwidth utilization for this interface',
 *     'Check for errors on this interface'
 *   ]
 * });
 */
function useAiContext(context) {
  useEffect(() => {
    // Validate required props
    if (!context || typeof context !== 'object') {
      console.warn('useAiContext: context must be an object');
      return undefined;
    }

    if (!context.type || !context.name || !context.context) {
      console.warn('useAiContext: context must have type, name, and context properties');
      return undefined;
    }

    // Add the context
    $kaizen.addChatContext(context);

    // Cleanup function - remove the context when component unmounts
    return () => {
      $kaizen.removeChatContext(context);
    };
  }, [context]); // Re-run if context changes
}

export default useAiContext;
